import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Users, Star, ShoppingBag, ArrowLeft, Search, TrendingUp, Building2, User, Key, X, Trash2, Pencil, Save, Phone, Mail, MapPin, Calendar, CreditCard, ShieldCheck, Megaphone } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("points");
  const [filterType, setFilterType] = useState("all");
  
  // Reset password modal
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [resetting, setResetting] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  // Edit customer modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.get(`${API}/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error("Errore caricamento clienti:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers
    .filter(c => {
      const matchesSearch = c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.includes(searchTerm) ||
        c.business_data?.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtra per tipo
      const isBusiness = c.account_type === "business";
      const matchesType = filterType === "all" ? true : 
        filterType === "private" ? !isBusiness : 
        filterType === "business" ? isBusiness :
        filterType === "marketing" ? c.consent_marketing === true : true;
      
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "points":
          return (b.points || 0) - (a.points || 0);
        case "name":
          return (a.name || "").localeCompare(b.name || "");
        case "orders":
          return (b.orders_count || 0) - (a.orders_count || 0);
        case "spent":
          return (b.total_spent || 0) - (a.total_spent || 0);
        default:
          return 0;
      }
    });

  const privateCustomers = customers.filter(c => c.account_type !== "business");
  const businessCustomers = customers.filter(c => c.account_type === "business");
  const marketingCustomers = customers.filter(c => c.consent_marketing === true);
  
  const currentCustomers = filterType === "all" ? customers : 
    filterType === "private" ? privateCustomers : 
    filterType === "business" ? businessCustomers :
    filterType === "marketing" ? marketingCustomers : customers;
  const totalPoints = currentCustomers.reduce((sum, c) => sum + (c.points || 0), 0);
  const totalSpent = currentCustomers.reduce((sum, c) => sum + (c.total_spent || 0), 0);

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (confirmation !== "CONFERMO") {
      toast.error("Scrivi 'CONFERMO' per procedere");
      return;
    }
    
    setResetting(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/customers/${selectedCustomer.id}/reset-password`, {
        customer_id: selectedCustomer.id,
        new_password: newPassword,
        confirmation: confirmation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Password reimpostata per ${selectedCustomer.email}`);
      setShowResetModal(false);
      setSelectedCustomer(null);
      setNewPassword("");
      setConfirmation("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel reset password");
    } finally {
      setResetting(false);
    }
  };

  const openResetModal = (customer) => {
    setSelectedCustomer(customer);
    setNewPassword("");
    setConfirmation("");
    setShowResetModal(true);
  };

  const handleRecalculateStats = async () => {
    if (!window.confirm("Vuoi ricalcolare le statistiche di tutti i clienti? Questa operazione potrebbe richiedere qualche secondo.")) {
      return;
    }
    
    setRecalculating(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/recalculate-customer-stats`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "Statistiche ricalcolate!");
      fetchCustomers(); // Ricarica i dati
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel ricalcolo");
    } finally {
      setRecalculating(false);
    }
  };

  const handleDeleteCustomer = async (customer) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il cliente "${customer.name}"?\n\nQuesta azione è irreversibile.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/customers/${customer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Cliente ${customer.name} eliminato`);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const openEditModal = (customer) => {
    setEditCustomer(customer);
    setEditData({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      birthdate: customer.birthdate || "",
      fiscal_code: customer.fiscal_code || "",
      points: customer.points || 0,
      account_type: customer.account_type || "private",
      business_data: customer.business_data ? { ...customer.business_data } : {
        company_name: "",
        vat_number: "",
        fiscal_code: "",
        pec: "",
        sdi_code: "",
        company_address: ""
      }
    });
    setShowEditModal(true);
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleBusinessDataChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      business_data: { ...prev.business_data, [field]: value }
    }));
  };

  const handleSaveCustomer = async () => {
    if (!editData.name || !editData.email) {
      toast.error("Nome e email sono obbligatori");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/admin/customers/${editCustomer.id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Cliente ${editData.name} aggiornato`);
      setShowEditModal(false);
      setEditCustomer(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <header className="bg-zinc-900 border-b border-zinc-800 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Users className="w-7 h-7 text-green-500" />
              Clienti Iscritti
            </h1>
          </div>
          <span className="text-zinc-400">
            {privateCustomers.length} privati / {businessCustomers.length} aziende
          </span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Type Toggle */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <p className="text-white font-semibold">Tipo clienti:</p>
            <div className="flex bg-black rounded-lg p-1">
              <button
                onClick={() => setFilterType("all")}
                className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  filterType === "all" ? "bg-green-500 text-black" : "text-zinc-400"
                }`}
              >
                <Users className="w-4 h-4" />
                Tutti ({customers.length})
              </button>
              <button
                onClick={() => setFilterType("private")}
                className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  filterType === "private" ? "bg-green-500 text-black" : "text-zinc-400"
                }`}
              >
                <User className="w-4 h-4" />
                Privati ({privateCustomers.length})
              </button>
              <button
                onClick={() => setFilterType("business")}
                className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  filterType === "business" ? "bg-green-500 text-black" : "text-zinc-400"
                }`}
              >
                <Building2 className="w-4 h-4" />
                Aziende ({businessCustomers.length})
              </button>
              <button
                onClick={() => setFilterType("marketing")}
                className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  filterType === "marketing" ? "bg-yellow-500 text-black" : "text-zinc-400"
                }`}
                data-testid="filter-marketing"
              >
                <Megaphone className="w-4 h-4" />
                Marketing ({marketingCustomers.length})
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-zinc-400 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">{filterType === "all" ? "Totale" : (filterType === "private" ? "Privati" : "Aziende")}</span>
            </div>
            <p className="text-2xl font-bold text-white">{currentCustomers.length}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-zinc-400 mb-1">
              <Star className="w-4 h-4" />
              <span className="text-sm">Punti Totali</span>
            </div>
            <p className="text-2xl font-bold text-green-500">{totalPoints.toLocaleString()}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-zinc-400 mb-1">
              <ShoppingBag className="w-4 h-4" />
              <span className="text-sm">Ordini Totali</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {currentCustomers.reduce((sum, c) => sum + (c.orders_count || 0), 0)}
            </p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-zinc-400 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Fatturato Clienti</span>
            </div>
            <p className="text-2xl font-bold text-yellow-500">€{totalSpent.toFixed(2)}</p>
          </div>
        </div>

        {/* Pulsante ricalcola statistiche */}
        <div className="mb-6">
          <button
            onClick={handleRecalculateStats}
            disabled={recalculating}
            className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {recalculating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Ricalcolo in corso...
              </>
            ) : (
              <>
                <TrendingUp className="w-4 h-4" />
                Ricalcola statistiche clienti
              </>
            )}
          </button>
          <p className="text-zinc-500 text-xs mt-1">
            Aggiorna fatturato e numero ordini basandosi sugli ordini completati
          </p>
        </div>

        {/* Search & Sort */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              placeholder="Cerca per nome, email o telefono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:border-green-500"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
          >
            <option value="points">Ordina per Punti</option>
            <option value="name">Ordina per Nome</option>
            <option value="orders">Ordina per Ordini</option>
            <option value="spent">Ordina per Speso</option>
          </select>
        </div>

        {/* Customers List */}
        {filteredCustomers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">Nessun cliente trovato</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredCustomers.map((customer) => (
              <div
                key={customer.id}
                className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-zinc-700 transition-all"
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold text-white">{customer.name}</h3>
                      {customer.account_type !== "business" && (
                        <span className="bg-green-500 text-black text-sm font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          {customer.points || 0} pt
                        </span>
                      )}
                      {customer.account_type === "business" && (
                        <span className="bg-blue-500 text-white text-sm font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          Azienda
                        </span>
                      )}
                      {customer.partner_company_name && (
                        <span className="bg-purple-500/20 text-purple-400 text-sm font-medium px-2 py-0.5 rounded-full flex items-center gap-1 border border-purple-500/30">
                          <Building2 className="w-3 h-3" />
                          {customer.partner_company_name}
                        </span>
                      )}
                    </div>
                    {customer.account_type === "business" && customer.business_data && (
                      <p className="text-green-500 font-semibold text-sm mb-1">
                        {customer.business_data.company_name}
                      </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-sm">
                      <p className="text-zinc-400 flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-zinc-500" />
                        {customer.email}
                      </p>
                      <p className="text-zinc-400 flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-zinc-500" />
                        {customer.phone || "-"}
                      </p>
                      {customer.address && (
                        <p className="text-zinc-400 flex items-center gap-1.5 sm:col-span-2">
                          <MapPin className="w-3.5 h-3.5 text-zinc-500" />
                          {customer.address}
                        </p>
                      )}
                      {customer.birthdate && (
                        <p className="text-zinc-400 flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-zinc-500" />
                          {new Date(customer.birthdate).toLocaleDateString("it-IT")}
                        </p>
                      )}
                      {customer.account_type !== "business" && (
                        <p className="text-zinc-400 flex items-center gap-1.5">
                          <CreditCard className="w-3.5 h-3.5 text-zinc-500" />
                          CF: <span className={customer.fiscal_code ? "text-white" : "text-red-400"}>{customer.fiscal_code || "Mancante"}</span>
                        </p>
                      )}
                    </div>
                    {customer.account_type === "business" && customer.business_data && (
                      <div className="mt-2 p-3 bg-zinc-800 rounded-lg">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-zinc-500">P.IVA: </span>
                            <span className="text-white">{customer.business_data.vat_number || "-"}</span>
                          </div>
                          <div>
                            <span className="text-zinc-500">Cod. SDI: </span>
                            <span className="text-white">{customer.business_data.sdi_code || "-"}</span>
                          </div>
                          <div>
                            <span className="text-zinc-500">PEC: </span>
                            <span className="text-white">{customer.business_data.pec || "-"}</span>
                          </div>
                          <div>
                            <span className="text-zinc-500">Cod. Fiscale: </span>
                            <span className="text-white">{customer.business_data.fiscal_code || "-"}</span>
                          </div>
                          <div className="sm:col-span-2">
                            <span className="text-zinc-500">Sede Legale: </span>
                            <span className="text-white">{customer.business_data.company_address || customer.business_data.legal_address || "-"}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex gap-6 text-sm">
                      <div className="text-center">
                        <p className="text-zinc-500">Ordini</p>
                        <p className="text-white font-bold">{customer.orders_count || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-zinc-500">Speso</p>
                        <p className="text-yellow-500 font-bold">€{(customer.total_spent || 0).toFixed(2)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-zinc-500">Iscritto</p>
                        <p className="text-white">
                          {customer.created_at 
                            ? new Date(customer.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })
                            : "-"
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => openEditModal(customer)}
                        className="bg-green-500/20 hover:bg-green-500/40 text-green-500 p-2 rounded-lg transition-colors"
                        title="Modifica cliente"
                        data-testid={`edit-customer-${customer.id}`}
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => openResetModal(customer)}
                        className="bg-zinc-800 hover:bg-zinc-700 text-white p-2 rounded-lg transition-colors"
                        title="Reset Password"
                      >
                        <Key className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteCustomer(customer)}
                        className="bg-red-500/20 hover:bg-red-500/40 text-red-500 p-2 rounded-lg transition-colors"
                        title="Elimina cliente"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Reset Password Modal */}
      {showResetModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Key className="w-6 h-6 text-yellow-500" />
                Reset Password
              </h2>
              <button
                onClick={() => setShowResetModal(false)}
                className="text-zinc-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="bg-zinc-800 rounded-lg p-3 mb-4">
              <p className="text-white font-semibold">{selectedCustomer.name}</p>
              <p className="text-zinc-400 text-sm">{selectedCustomer.email}</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white font-semibold mb-2">Nuova Password</label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimo 6 caratteri"
                  className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                />
              </div>
              
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-400 text-sm mb-2">
                  Questa azione sovrascriverà la password attuale del cliente.
                  Procedi SOLO su richiesta del cliente.
                </p>
                <label className="block text-white font-semibold mb-2">
                  Scrivi "CONFERMO" per procedere
                </label>
                <input
                  type="text"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value.toUpperCase())}
                  placeholder="CONFERMO"
                  className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResetModal(false)}
                  className="flex-1 bg-zinc-800 text-white py-3 rounded-lg font-semibold hover:bg-zinc-700"
                >
                  Annulla
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={resetting || confirmation !== "CONFERMO" || newPassword.length < 6}
                  className="flex-1 bg-yellow-500 text-black py-3 rounded-lg font-bold hover:bg-yellow-600 disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed"
                >
                  {resetting ? "Resettando..." : "Reset Password"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      {showEditModal && editCustomer && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-2xl w-full my-8" data-testid="edit-customer-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Pencil className="w-6 h-6 text-green-500" />
                Modifica Cliente
              </h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-zinc-400 hover:text-white"
                data-testid="close-edit-modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              {/* Tipo account (read-only info) */}
              <div className="bg-zinc-800 rounded-lg p-3 flex items-center gap-2">
                {editData.account_type === "business" ? (
                  <><Building2 className="w-5 h-5 text-blue-500" /><span className="text-blue-400 font-semibold">Account Aziendale</span></>
                ) : (
                  <><User className="w-5 h-5 text-green-500" /><span className="text-green-400 font-semibold">Account Privato</span></>
                )}
                <span className="text-zinc-500 text-sm ml-auto">ID: {editCustomer.id.slice(0, 8)}...</span>
              </div>

              {/* Dati personali */}
              <div className="border border-zinc-800 rounded-lg p-4">
                <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">Dati Personali</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Nome completo *</label>
                    <input
                      type="text"
                      value={editData.name}
                      onChange={(e) => handleEditChange("name", e.target.value)}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                      data-testid="edit-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Email *</label>
                    <input
                      type="email"
                      value={editData.email}
                      onChange={(e) => handleEditChange("email", e.target.value)}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                      data-testid="edit-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Telefono</label>
                    <input
                      type="tel"
                      value={editData.phone}
                      onChange={(e) => handleEditChange("phone", e.target.value)}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                      data-testid="edit-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Data di nascita</label>
                    <input
                      type="date"
                      value={editData.birthdate}
                      onChange={(e) => handleEditChange("birthdate", e.target.value)}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                      data-testid="edit-birthdate-input"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-zinc-400 text-sm mb-1">Indirizzo</label>
                    <input
                      type="text"
                      value={editData.address}
                      onChange={(e) => handleEditChange("address", e.target.value)}
                      placeholder="Via e numero civico, città"
                      className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                      data-testid="edit-address-input"
                    />
                  </div>
                </div>
              </div>

              {/* Codice Fiscale per privati */}
              {editData.account_type !== "business" && (
                <div className="border border-zinc-800 rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">Fatturazione</h3>
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Codice Fiscale</label>
                    <input
                      type="text"
                      value={editData.fiscal_code}
                      onChange={(e) => handleEditChange("fiscal_code", e.target.value.toUpperCase())}
                      placeholder="RSSMRA85M01H501Z"
                      maxLength={16}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white uppercase focus:outline-none focus:border-green-500"
                      data-testid="edit-fiscal-code-input"
                    />
                    {!editData.fiscal_code && (
                      <p className="text-yellow-500 text-xs mt-1">Codice fiscale mancante - inseriscilo per la fatturazione</p>
                    )}
                  </div>
                </div>
              )}

              {/* Dati aziendali per business */}
              {editData.account_type === "business" && (
                <div className="border border-zinc-800 rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">Dati Aziendali</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-zinc-400 text-sm mb-1">Ragione Sociale</label>
                      <input
                        type="text"
                        value={editData.business_data?.company_name || ""}
                        onChange={(e) => handleBusinessDataChange("company_name", e.target.value)}
                        className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                        data-testid="edit-company-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">P.IVA</label>
                      <input
                        type="text"
                        value={editData.business_data?.vat_number || ""}
                        onChange={(e) => handleBusinessDataChange("vat_number", e.target.value)}
                        className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                        data-testid="edit-vat-number-input"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">Codice Fiscale</label>
                      <input
                        type="text"
                        value={editData.business_data?.fiscal_code || ""}
                        onChange={(e) => handleBusinessDataChange("fiscal_code", e.target.value.toUpperCase())}
                        className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white uppercase focus:outline-none focus:border-green-500"
                        data-testid="edit-business-fiscal-code-input"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">PEC</label>
                      <input
                        type="email"
                        value={editData.business_data?.pec || ""}
                        onChange={(e) => handleBusinessDataChange("pec", e.target.value)}
                        className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                        data-testid="edit-pec-input"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-sm mb-1">Codice SDI</label>
                      <input
                        type="text"
                        value={editData.business_data?.sdi_code || ""}
                        onChange={(e) => handleBusinessDataChange("sdi_code", e.target.value)}
                        maxLength={7}
                        className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                        data-testid="edit-sdi-code-input"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-zinc-400 text-sm mb-1">Sede Legale</label>
                      <input
                        type="text"
                        value={editData.business_data?.company_address || ""}
                        onChange={(e) => handleBusinessDataChange("company_address", e.target.value)}
                        placeholder="Via Roma 1, 00100 Roma (RM)"
                        className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                        data-testid="edit-company-address-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Punti fedeltà (solo privati) */}
              {editData.account_type !== "business" && (
                <div className="border border-zinc-800 rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">Punti Fedeltà</h3>
                  <div className="flex items-center gap-3">
                    <Star className="w-5 h-5 text-green-500" />
                    <input
                      type="number"
                      value={editData.points}
                      onChange={(e) => handleEditChange("points", parseInt(e.target.value) || 0)}
                      min="0"
                      className="w-32 bg-black border border-zinc-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-green-500"
                      data-testid="edit-points-input"
                    />
                    <span className="text-zinc-400 text-sm">punti</span>
                  </div>
                </div>
              )}

              {/* Statistiche (read-only) */}
              <div className="border border-zinc-800 rounded-lg p-4 bg-zinc-800/50">
                <h3 className="text-zinc-500 font-semibold mb-3 text-sm uppercase tracking-wider">Statistiche (non modificabili)</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-zinc-500 text-xs">Ordini</p>
                    <p className="text-white font-bold text-lg">{editCustomer.orders_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs">Fatturato</p>
                    <p className="text-yellow-500 font-bold text-lg">€{(editCustomer.total_spent || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs">Iscritto il</p>
                    <p className="text-white font-bold text-sm">
                      {editCustomer.created_at ? new Date(editCustomer.created_at).toLocaleDateString("it-IT") : "-"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Consensi GDPR (read-only) */}
              <div className="border border-zinc-800 rounded-lg p-4 bg-zinc-800/50">
                <h3 className="text-zinc-500 font-semibold mb-3 text-sm uppercase tracking-wider">Consensi GDPR</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-green-500" />
                      <span className="text-zinc-300 text-sm">Privacy Policy</span>
                    </div>
                    <div className="text-right">
                      {editCustomer.consent_privacy ? (
                        <span className="text-green-500 text-sm font-medium">
                          Accettata {editCustomer.consent_privacy_date ? `il ${new Date(editCustomer.consent_privacy_date).toLocaleDateString("it-IT")}` : ""}
                        </span>
                      ) : (
                        <span className="text-zinc-500 text-sm">Non registrato</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Megaphone className="w-4 h-4 text-yellow-500" />
                      <span className="text-zinc-300 text-sm">Marketing</span>
                    </div>
                    <div className="text-right">
                      {editCustomer.consent_marketing ? (
                        <span className="text-green-500 text-sm font-medium">
                          Accettato {editCustomer.consent_marketing_date ? `il ${new Date(editCustomer.consent_marketing_date).toLocaleDateString("it-IT")}` : ""}
                        </span>
                      ) : (
                        <span className="text-red-400 text-sm">Non accettato</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 mt-6 pt-4 border-t border-zinc-800">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 bg-zinc-800 text-white py-3 rounded-lg font-semibold hover:bg-zinc-700 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveCustomer}
                disabled={saving}
                className="flex-1 bg-green-500 text-black py-3 rounded-lg font-bold hover:bg-green-600 disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                data-testid="save-customer-btn"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Salva Modifiche
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
